import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type FC,
  type MutableRefObject,
} from 'react'
import cn from 'classnames'
import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FunnelPlotOutlined,
  InboxOutlined,
  SaveOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  RollbackOutlined,
} from '@ant-design/icons'
import { Empty, Tabs, TabsProps } from 'antd'

import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import gclasses from '../../../CRMSettings/global.module.css'
import classes from './index.module.css'

import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import Preloader from '@UI/Preloader'

import type { Field, LeadState } from '@/api/types/crm/crm.types'
import { crmApi, crmTrafficChannelApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import {
  getLeadFieldValue,
  getLeadVkLinkFormValue,
} from '../../utils/leadFields'
import { useCRMContext } from '@pages/Admin/CRM/CRMContext'
import {
  ignoreLeadFields,
  validateForm,
} from '../AddNewLeadSelect/validateLeadData'
import {
  appendCrmLeadFieldBindingSettingsHint,
  extractCrmLeadFieldSaveErrorMessages,
  isMessengerIdentityFieldUniqueName,
  mapMessengerIdentityFieldSaveErrors,
  type MessengerIdentityFieldUniqueName,
} from '../../utils/messengerAccountFields'

import { useMedia } from 'react-use'

import MainInfoBlock from './blocks/MainInfoBlock/MainInfoBlock'
import LeadFunnelMetaRow from './blocks/MainInfoBlock/LeadFunnelMetaRow'
import StatusListBlock from './blocks/StatusListBlock/StatusListBlock'
import TrafficChannelBlock from '@pages/Admin/CRM/components/FunnelLeadsTable/components/TrafficChannelBlock'
import TabBindingStudAccount from './components/TabBindingStudAccount/TabBindingStudAccount'
import {
  LeadStatusProvider,
  useLeadStatuses,
} from './blocks/StatusListBlock/LeadStatusContext'
import dayjs from 'dayjs'
import useFullLeadModalAccess from './hooks/useFullLeadModalAccess'
import { useLeadRefetch } from './LeadRefetchContext'
import { resolveLeadWorkflowState } from './leadFunnelId.utils'
import {
  CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS,
  useRefetchOnBrowserTabVisible,
} from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'
import debounce from '@/utils/debounce/debounce'
import { normalizeCrmFieldSelectOptions } from '@pages/Admin/CRM/utils/crmFieldSelectOptions'

import NarrowAboutContent from './tabs/About/NarrowAboutContent'
import WideAboutContent from './tabs/About/WideAboutContent'
import WideShoppingContent from './tabs/Shopping/WideShoppingContent'
import WideHistoryContent from './tabs/History/WideHistoryContent'
import UtmTagsContent from './tabs/Utm/UtmTagsContent'

import type {
  LeadProfileLead,
  LeadProfileModalContentProps,
  LeadProfileModalContentRef,
} from './LeadProfileModal.types'

const LeadStatusRefreshBridge: FC<{
  refreshRef: MutableRefObject<(() => void) | null>
}> = ({ refreshRef }) => {
  const { refreshStatuses } = useLeadStatuses()
  useEffect(() => {
    refreshRef.current = refreshStatuses
  }, [refreshRef, refreshStatuses])
  return null
}

const antIconPointerProps = {
  onPointerEnterCapture: undefined,
  onPointerLeaveCapture: undefined,
} as const

const LeadProfileModalContent = forwardRef<
  LeadProfileModalContentRef,
  LeadProfileModalContentProps
>(
  (
    {
      lead,
      setOpenModal,
      open,
      onClose,
      onClickByChildren,
      onKanbanCardSync,
      onOpenVkChatWithLead,
      onOpenMaxChatWithLead,
      onOpenTelegramChatWithLead,
      onOpenWazzupChatWithLead,
    },
    ref,
  ) => {
    const addAlert = useAddAlert()
    const { currentRole, ...access } = useFullLeadModalAccess()
    const { preloadingData } = useCRMContext()
    const isNarrowLeadHeader = useMedia('(max-width: 767px)', false)
    const {
      handleRefetchLeadInModal,
      handleRefetchLeadsOnClose,
      isRefetchingLeadCard,
      selectedFunnelId,
      selectFunnel,
      selectedFunnelIdRef,
      committedManagerId,
      committedNextContactAt,
      committedCurrentStatusId,
      initialFunnelIdForRefetch,
      resolveLeadFunnelId,
      getCardInFunnelSnapshot,
      cardFunnelSnapshotEpoch,
    } = useLeadRefetch()

    const leadStateForWorkflow = useMemo(
      () =>
        resolveLeadWorkflowState({
          selectedFunnelId,
          getCardInFunnelSnapshot,
          isRefetchingLeadCard,
          globalType: lead.type as LeadState | undefined,
        }),
      [
        cardFunnelSnapshotEpoch,
        getCardInFunnelSnapshot,
        isRefetchingLeadCard,
        lead.type,
        selectedFunnelId,
      ],
    )

    const funnelNextContactDraftRef = useRef('')
    const statusListRefreshRef = useRef<(() => void) | null>(null)
    const latestFunnelStatusIdRef = useRef<number | null>(null)
    const syncFunnelStatusDraft = useCallback((id: number | null) => {
      latestFunnelStatusIdRef.current = id
    }, [])

    const syncKanbanCardAfterMutation = useCallback(() => {
      const id = resolveLeadFunnelId()
      if (id != null && Number.isFinite(id) && id > 0) {
        onKanbanCardSync?.(id)
      }
    }, [resolveLeadFunnelId, onKanbanCardSync])

    const tabs: TabsProps['items'] = [
      { label: 'О заявке', key: '1' },
      { label: 'Покупки', key: '2' },
      { label: 'История изменений', key: '3' },
      { label: 'UTM метки', key: '4' },
    ]

    const studAccountTabBarExtra = !!lead && (
      <div className={classes.tabsBarStudAccount}>
        <TabBindingStudAccount
          key={lead.user_id != null ? String(lead.user_id) : 'unbound'}
          lead={lead}
          onOpenStudentAccount={() =>
            lead.user_id && onClickByChildren(lead.user_id)
          }
          onOpenAddStudAccount={() =>
            setOpenModal({ type: 'add-stud-account' })
          }
          onRefetchLead={() => {
            setOpenModal({ type: null })
            setHistoryRefreshKey((k) => k + 1)
            void handleRefetchLeadInModal(
              selectedFunnelId ?? initialFunnelIdForRefetch,
            )
            handleRefetchLeadsOnClose()
          }}
        />
      </div>
    )

    const [selectTabKey, setSelectTabKey] = useState<string>('1')
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0)
    const [selectedManagerId, setSelectedManagerId] = useState<number | null>(
      null,
    )
    const selectedManagerIdRef = useRef<number | null>(null)
    const isManagerExplicitlyClearedRef = useRef(false)
    const isManagerDirtyRef = useRef(false)

    const [leadFields, setLeadFields] = useState<Field[]>([])
    const [isLoadingLeadFields, setIsLoadingLeadFields] = useState(false)
    const leadFieldsRequestIdRef = useRef(0)
    const leadHasFunnels =
      Array.isArray(lead?.funnels) && lead.funnels.length > 0
    const selectedFunnelIdRefForFields = useRef(selectedFunnelId)
    selectedFunnelIdRefForFields.current = selectedFunnelId
    const leadHasFunnelsRef = useRef(leadHasFunnels)
    leadHasFunnelsRef.current = leadHasFunnels
    const openRef = useRef(open)
    openRef.current = open

    useEffect(() => {
      let isCancelled = false

      if (!open) {
        setIsLoadingLeadFields(false)
        return
      }
      if (leadHasFunnels && selectedFunnelId === null) {
        setIsLoadingLeadFields(false)
        return
      }

      setIsLoadingLeadFields(true)
      const requestId = ++leadFieldsRequestIdRef.current

      crmApi
        .getAllFields(
          selectedFunnelId !== null && Number.isFinite(selectedFunnelId)
            ? { funnel_id: selectedFunnelId }
            : undefined,
        )
        .then((res) => {
          if (isCancelled || requestId !== leadFieldsRequestIdRef.current) return
          if (isError(res)) throw new Error()
          setLeadFields(res?.data?.data ?? [])
        })
        .catch(() => {
          if (isCancelled || requestId !== leadFieldsRequestIdRef.current) return
          addAlert({
            title: 'Ошибка',
            text: 'Ошибка загрузки полей для выбранной воронки',
            type: 'error',
          })
        })
        .finally(() => {
          if (isCancelled || requestId !== leadFieldsRequestIdRef.current) return
          setIsLoadingLeadFields(false)
        })

      return () => {
        isCancelled = true
      }
    }, [open, selectedFunnelId, leadHasFunnels])

    const debouncedLeadFieldsVisibleRefetchRef = useRef(
      debounce(() => {
        if (!openRef.current) return
        if (leadHasFunnelsRef.current && selectedFunnelIdRefForFields.current === null) {
          return
        }

        const funnelId = selectedFunnelIdRefForFields.current
        const requestId = ++leadFieldsRequestIdRef.current

        crmApi
          .getAllFields(
            funnelId !== null && Number.isFinite(funnelId)
              ? { funnel_id: funnelId }
              : undefined,
          )
          .then((res) => {
            if (requestId !== leadFieldsRequestIdRef.current) return
            if (isError(res)) return

            const nextFields = res?.data?.data ?? []
            setLeadFields((prev) => {
              if (!prev.length) return nextFields

              return nextFields.map((field) => {
                if (field.type !== 'select') return field
                const nextOptions = normalizeCrmFieldSelectOptions(
                  field.value_options,
                )
                if (nextOptions.length) return field

                const prevField = prev.find(
                  (item) =>
                    item.id === field.id ||
                    item.unique_name === field.unique_name,
                )
                const prevOptions = normalizeCrmFieldSelectOptions(
                  prevField?.value_options,
                )
                if (!prevOptions.length) return field

                return { ...field, value_options: prevOptions }
              })
            })
          })
          .catch(() => {
            // тихий refetch — без алертов и без лоадера
          })
      }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS),
    )

    // Каталог полей (value_options/цвета select) не приходит в get-lead —
    // при возврате на вкладку обновляем без blur/Preloader.
    useRefetchOnBrowserTabVisible(() => {
      debouncedLeadFieldsVisibleRefetchRef.current()
    }, open)

    // fields data — синхрон с `lead.fields` после get-lead (тот же lead.id)
    const skipFields = ['STATUS', 'DATEL', 'DATEZ']
    const [formData, setFormData] = useState<Record<string, string>>({})
    const [identityFieldErrors, setIdentityFieldErrors] = useState<
      Partial<Record<MessengerIdentityFieldUniqueName, string>>
    >({})

    const [trafficChannelDraftId, setTrafficChannelDraftId] = useState<
      number | null
    >(null)

    const leadProfileFieldsFingerprint = useMemo(() => {
      return (
        lead?.fields
          ?.filter((field) => !skipFields.includes(field.unique_name))
          .map(
            (field) => `${field.unique_name}\u0000${String(field.value ?? '')}`,
          )
          .sort()
          .join('\u0001') ?? ''
      )
    }, [lead?.fields])

    const leadVkLinkForForm = getLeadVkLinkFormValue(lead)

    const buildFormDataFromLead = useCallback(
      (leadSource: LeadProfileLead | undefined) => {
        const defaultData =
          leadSource?.fields
            ?.filter((field) => !skipFields.includes(field.unique_name))
            .reduce((acc, field) => {
              acc[field.unique_name] = String(field.value ?? '')
              return acc
            }, {} as Record<string, string>) || {}

        const vkLink = getLeadVkLinkFormValue(leadSource)
        if (vkLink) {
          defaultData.VK_LINK = vkLink
        }

        return defaultData
      },
      [],
    )

    useEffect(() => {
      setFormData(buildFormDataFromLead(lead))
      // Не зависеть от всего объекта `lead`: patchLead (например is_collapsed)
      // меняет ссылку и иначе затирает несохранённый ввод в formData.
    }, [
      buildFormDataFromLead,
      lead?.id,
      leadProfileFieldsFingerprint,
      leadVkLinkForForm,
    ])

    useEffect(() => {
      if (!lead) return
      setTrafficChannelDraftId(lead.traffic_channel?.id ?? null)
    }, [lead?.id, lead?.traffic_channel?.id])

    useEffect(() => {
      if (!lead) {
        setSelectedManagerId(null)
        selectedManagerIdRef.current = null
        isManagerExplicitlyClearedRef.current = false
        isManagerDirtyRef.current = false
        return
      }
      const next =
        selectedFunnelId !== null
          ? committedManagerId
          : lead.manager?.id ?? null

      // После server-side действий (например bulk-назначение менеджера) важно
      // синхронизировать селектор по актуальным данным воронки, даже если локально
      // ранее было "грязное" значение.
      if (next === selectedManagerIdRef.current && isManagerDirtyRef.current)
        return

      setSelectedManagerId(next)
      selectedManagerIdRef.current = next
      isManagerExplicitlyClearedRef.current = false
      isManagerDirtyRef.current = false
    }, [lead, selectedFunnelId, committedManagerId])

    const handleSelectManager = useCallback((managerId: number | null) => {
      setSelectedManagerId(managerId)
      selectedManagerIdRef.current = managerId
      isManagerDirtyRef.current = true
      if (managerId !== null) {
        isManagerExplicitlyClearedRef.current = false
      }
    }, [])

    const handleClearManager = useCallback(() => {
      isManagerExplicitlyClearedRef.current = true
      isManagerDirtyRef.current = true
      setSelectedManagerId(null)
      selectedManagerIdRef.current = null
    }, [])

    const handleChangeFieldData = (uniqueName: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [uniqueName]: value,
      }))
    }

    const handleClearIdentityFieldError = useCallback(
      (uniqueName: MessengerIdentityFieldUniqueName) => {
        setIdentityFieldErrors((prev) => {
          if (!prev[uniqueName]) {
            return prev
          }

          const next = { ...prev }
          delete next[uniqueName]
          return next
        })
      },
      [],
    )

    const handleMoveToInWorkClick = useCallback(() => {
      const hasRealFunnel = (lead?.funnels ?? []).some((funnel) => {
        const id = Number(funnel?.id)
        return Number.isFinite(id) && id > 0
      })

      if (!hasRealFunnel) {
        addAlert({
          title: 'Ошибка',
          text: 'Сначала добавьте воронку',
          type: 'error',
        })
        return
      }

      setOpenModal({ type: 'change-lead-state' })
    }, [addAlert, lead?.funnels, setOpenModal])

    useImperativeHandle(ref, () => ({
      handleChangeFieldData,
      refreshHistory: () => {
        setHistoryRefreshKey((prev) => prev + 1)
      },
    }))

    const handlerSaveData = async () => {
      if (!lead) {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось сохранить данные, лид не найден',
          type: 'error',
        })
        return
      }

      const editableFields = leadFields.filter(
        (field) => !ignoreLeadFields.includes(field.unique_name),
      )
      const resultValidation = validateForm(formData, editableFields)
      if (resultValidation !== true) {
        const leadFieldsByUniqueName = new Map(
          leadFields.map((field) => [field.unique_name, field.name]),
        )
        const nextIdentityFieldErrors = Object.fromEntries(
          Object.entries(resultValidation).filter(([key]) =>
            isMessengerIdentityFieldUniqueName(key),
          ),
        ) as Partial<Record<MessengerIdentityFieldUniqueName, string>>

        if (Object.keys(nextIdentityFieldErrors).length > 0) {
          setIdentityFieldErrors((prev) => ({
            ...prev,
            ...nextIdentityFieldErrors,
          }))
        }

        addAlert({
          title: 'Ошибка',
          text: Object.entries(resultValidation).map(
            ([key, value]) =>
              `${leadFieldsByUniqueName.get(key) || key}: ${value}`,
          ),
          type: 'error',
        })
        return
      }

      const canEditManagerInCard = ['admin', 'head-of-sales'].includes(
        currentRole,
      )
      const shouldUpdateManager =
        canEditManagerInCard && selectedFunnelId !== null
      const resolvedFunnelId: number | null =
        selectedFunnelIdRef.current !== null &&
        Number.isFinite(selectedFunnelIdRef.current) &&
        selectedFunnelIdRef.current > 0
          ? selectedFunnelIdRef.current
          : null
      const editableFieldNames = new Set(
        editableFields.map((field) => field.unique_name),
      )
      const fieldsToSubmit = Object.fromEntries(
        Object.entries(formData).filter(([key]) => editableFieldNames.has(key)),
      )
      // ФИО правится в шапке карточки и не входит в поля воронки (в «Учениках»
      // поля NAME нет), поэтому шлём его отдельно и только если оно менялось.
      if (!editableFieldNames.has('NAME') && formData['NAME'] !== undefined) {
        const committedName = String(getLeadFieldValue(lead, 'NAME') ?? '')
        if (formData['NAME'] !== committedName) {
          fieldsToSubmit['NAME'] = formData['NAME']
        }
      }
      const updateLeadParams = {
        lead_id: lead.id,
        ...(resolvedFunnelId !== null ? { funnel_id: resolvedFunnelId } : {}),
      }

      /** MySQL DATETIME/TIMESTAMP: `YYYY-MM-DD HH:mm:ss`, без ISO (T/Z/мс). */
      const toNextContactApiValue = (
        raw: string | null | undefined,
      ): string | null => {
        if (raw == null || String(raw).trim() === '') return null
        const d = dayjs(String(raw).trim())
        return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : null
      }

      const committedNextContactRaw = committedNextContactAt

      const draftNextContactRaw =
        funnelNextContactDraftRef.current?.trim() ?? ''
      const committedApi = toNextContactApiValue(committedNextContactRaw)
      const draftApi = toNextContactApiValue(
        draftNextContactRaw === '' ? null : draftNextContactRaw,
      )
      const shouldUpdateNextContactAt =
        access.canEditFields &&
        selectedFunnelId !== null &&
        Number.isFinite(Number(selectedFunnelId)) &&
        Number(selectedFunnelId) > 0 &&
        draftApi !== committedApi

      const funnelCardPatch: {
        manager_id?: number | null
        next_contact_at?: string | null
      } = {}

      if (shouldUpdateManager) {
        funnelCardPatch.manager_id = isManagerExplicitlyClearedRef.current
          ? null
          : selectedManagerIdRef.current ?? null
      }
      if (shouldUpdateNextContactAt) {
        funnelCardPatch.next_contact_at = draftApi
      }

      const shouldPatchFunnelCard =
        selectedFunnelId !== null &&
        Number.isFinite(Number(selectedFunnelId)) &&
        Number(selectedFunnelId) > 0 &&
        Object.keys(funnelCardPatch).length > 0

      const committedTrafficChannelId = lead.traffic_channel?.id ?? null
      const shouldUpdateTrafficChannel =
        access.canEditFields &&
        trafficChannelDraftId != null &&
        trafficChannelDraftId !== committedTrafficChannelId

      const requests = [
        crmApi.updateLeadFields(updateLeadParams, { fields: fieldsToSubmit }),
        ...(shouldPatchFunnelCard
          ? [
              crmApi.leads.updateLeadCardInFunnel(
                { lead_id: lead.id, funnel_id: selectedFunnelId! },
                funnelCardPatch,
              ),
            ]
          : []),
        ...(shouldUpdateTrafficChannel
          ? [
              crmTrafficChannelApi.updateLeadChannel(
                lead.id,
                trafficChannelDraftId,
              ),
            ]
          : []),
      ]

      const committedStatusId = committedCurrentStatusId
      const draftStatusId = latestFunnelStatusIdRef.current
      const leadFunnelRowId = resolveLeadFunnelId()

      const shouldApplyStatusChange =
        access.canChangeLeadStatus &&
        leadFunnelRowId !== undefined &&
        draftStatusId !== null &&
        Number.isFinite(draftStatusId) &&
        draftStatusId > 0 &&
        draftStatusId !== committedStatusId

      try {
        if (shouldApplyStatusChange) {
          const ins = await crmApi.insertLeadStatus({
            status_id: draftStatusId,
            lead_funnel_id: leadFunnelRowId,
          })
          if (isError(ins)) throw new Error()
          statusListRefreshRef.current?.()
        }

        const responses = await Promise.all(requests)
        const failedResponse = responses.find((res) => isError(res))
        if (failedResponse) {
          const apiMessages = appendCrmLeadFieldBindingSettingsHint(
            extractCrmLeadFieldSaveErrorMessages({
              response: { data: failedResponse.data },
            }),
          )
          const mappedErrors = mapMessengerIdentityFieldSaveErrors(apiMessages)
          if (Object.keys(mappedErrors).length > 0) {
            setIdentityFieldErrors((prev) => ({ ...prev, ...mappedErrors }))
          }

          throw new Error(apiMessages[0] || 'Ошибка обновления данных')
        }

        addAlert({
          title: 'Успешно',
          text: 'Данные успешно обновлены',
          type: 'success',
        })
        setIdentityFieldErrors({})
        if (shouldApplyStatusChange) {
          setHistoryRefreshKey((prev) => prev + 1)
        }
        const refetchedLead = await handleRefetchLeadInModal(selectedFunnelId)
        if (refetchedLead) {
          setFormData(buildFormDataFromLead(refetchedLead))
        }
        handleRefetchLeadsOnClose()
        syncKanbanCardAfterMutation()
      } catch (error) {
        const apiMessages = appendCrmLeadFieldBindingSettingsHint(
          extractCrmLeadFieldSaveErrorMessages(error),
        )
        const mappedErrors = mapMessengerIdentityFieldSaveErrors(apiMessages)
        if (Object.keys(mappedErrors).length > 0) {
          setIdentityFieldErrors((prev) => ({ ...prev, ...mappedErrors }))
        }

        addAlert({
          title: 'Ошибка',
          text:
            apiMessages.join('\n') ||
            (error instanceof Error ? error.message : '') ||
            'Ошибка обновления данных',
          type: 'error',
        })
      }
    }

    const canUseFunnels = ['head-of-sales', 'admin', 'sales-manager'].includes(
      currentRole,
    )
    const canRemoveFunnelsInCard = [
      'head-of-sales',
      'admin',
      'sales-manager',
    ].includes(currentRole)
    const canEditManagerInCard = ['head-of-sales', 'admin'].includes(
      currentRole,
    )
    const canViewManagerInCard = [
      'head-of-sales',
      'admin',
      'sales-manager',
    ].includes(currentRole)
    const isLeadModalLoading = isRefetchingLeadCard || isLoadingLeadFields

    const shoppingCurrentLeadFunnelId = resolveLeadFunnelId()

    const renderStatusHistoryList = useCallback(() => {
      if (!lead) return null
      return (
        <StatusListBlock
          leadId={lead.id}
          dateL={getLeadFieldValue(lead, 'DATEL')}
          dateZ={getLeadFieldValue(lead, 'DATEZ')}
        />
      )
    }, [lead])

    return lead ? (
      <>
        <div className={classes.loadingWrapper}>
          <div className={cn(isLeadModalLoading && classes.loadingContentBlur)}>
            <div
              className={cn(mclasses.modalContainer, classes.modalContainer)}
            >
              <div className={mclasses.modalHeader}>
                <div className={mclasses.modalTitleContainer}>
                  <span
                    className={cn(
                      mclasses.modalHeaderTitle,
                      classes.modalHeaderTitle,
                    )}
                  >
                    {formData['NAME']?.trim() || '(без фио)'}
                    {access.canEditFields && (
                      <EditOutlined
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                        className={cn(gclasses.activeIcon, classes.editIcon)}
                        onClick={() => setOpenModal({ type: 'edit-name' })}
                      />
                    )}
                  </span>
                </div>
                <div className={mclasses.closeIcon} onClick={() => onClose()}>
                  <IcoRemove />
                </div>
              </div>

              <LeadStatusProvider
                leadId={lead.id}
                funnelId={selectedFunnelId}
                onNeedUpdateLead={handleRefetchLeadsOnClose}
              >
                <LeadStatusRefreshBridge refreshRef={statusListRefreshRef} />
                <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
                  <div className={classes.headerColumnWrapper}>
                    <div className={classes.leftSection}>
                      {isNarrowLeadHeader && (
                        <div
                          className={cn(
                            classes.modalTrafficMobile,
                            classes.trafficChannelPanelIsolation,
                          )}
                        >
                          <LeadFunnelMetaRow
                            lead={lead}
                            className={classes.trafficMetaRow}
                          />
                          <TrafficChannelBlock
                            layout="panel"
                            leadId={lead.id}
                            current={lead.traffic_channel}
                            channels={preloadingData.trafficChannels ?? []}
                            canEdit={access.canEditFields}
                            onKanbanCardSync={syncKanbanCardAfterMutation}
                            draftTrafficChannelId={trafficChannelDraftId}
                            onDraftTrafficChannelIdChange={
                              setTrafficChannelDraftId
                            }
                          />
                        </div>
                      )}
                      <MainInfoBlock
                        lead={lead}
                        fields={leadFields}
                        fieldsData={formData}
                        updateFieldData={handleChangeFieldData}
                        selectedManagerId={selectedManagerId}
                        onSelectManager={handleSelectManager}
                        onClearManager={handleClearManager}
                        canEditManager={canEditManagerInCard}
                        showManager={canViewManagerInCard}
                        deferStatusCommit
                        onFunnelStatusSelectionSync={syncFunnelStatusDraft}
                        funnelNextContactDraftRef={funnelNextContactDraftRef}
                        onOpenVkChatWithLead={onOpenVkChatWithLead}
                        onOpenMaxChatWithLead={onOpenMaxChatWithLead}
                        onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
                        onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
                        identityFieldErrors={identityFieldErrors}
                        onClearIdentityFieldError={handleClearIdentityFieldError}
                      />

                      <div
                        className={cn(classes.rightSection, classes.onlyMobile)}
                      >
                        <div className={classes.rightSectionColumn}>
                          <div className={classes.rightSectionScroll}>
                            {renderStatusHistoryList()}
                          </div>
                        </div>
                      </div>

                      <div className={classes.customTabsWrapper}>
                        <Tabs
                          items={tabs}
                          onChange={setSelectTabKey}
                          activeKey={selectTabKey ?? undefined}
                          className={classes.customTabs}
                          tabBarExtraContent={
                            studAccountTabBarExtra
                              ? { right: studAccountTabBarExtra }
                              : undefined
                          }
                        />
                      </div>

                      {selectTabKey === '1' && (
                        <NarrowAboutContent
                          lead={lead}
                          fields={leadFields}
                          fieldsData={formData}
                          updateFieldData={handleChangeFieldData}
                          inlineTextareasWhenNarrow={isNarrowLeadHeader}
                          onOpenVkChatWithLead={onOpenVkChatWithLead}
                          onOpenMaxChatWithLead={onOpenMaxChatWithLead}
                          onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
                          onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
                        />
                      )}
                    </div>

                    <div className={classes.rightSection}>
                      <div className={classes.rightSectionColumn}>
                        {!isNarrowLeadHeader && (
                          <div
                            className={cn(
                              classes.rightSectionTraffic,
                              classes.trafficChannelPanelIsolation,
                            )}
                          >
                            <LeadFunnelMetaRow
                              lead={lead}
                              className={classes.trafficMetaRow}
                            />
                            <TrafficChannelBlock
                              layout="panel"
                              leadId={lead.id}
                              current={lead.traffic_channel}
                              channels={preloadingData.trafficChannels ?? []}
                              canEdit={access.canEditFields}
                              onKanbanCardSync={syncKanbanCardAfterMutation}
                              draftTrafficChannelId={trafficChannelDraftId}
                              onDraftTrafficChannelIdChange={
                                setTrafficChannelDraftId
                              }
                            />
                          </div>
                        )}
                        <div className={classes.rightSectionScroll}>
                          {renderStatusHistoryList()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectTabKey === '1' && !isNarrowLeadHeader && (
                    <div className={classes.wideAboutRoot}>
                      <WideAboutContent
                        lead={lead}
                        fields={leadFields}
                        fieldsData={formData}
                        updateFieldData={handleChangeFieldData}
                        onOpenVkChatWithLead={onOpenVkChatWithLead}
                        onOpenMaxChatWithLead={onOpenMaxChatWithLead}
                        onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
                        onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
                      />
                    </div>
                  )}
                  {selectTabKey === '2' && (
                    <WideShoppingContent
                      lead={lead}
                      currentLeadFunnelId={shoppingCurrentLeadFunnelId}
                      onKanbanCardSync={syncKanbanCardAfterMutation}
                    />
                  )}
                  {selectTabKey === '3' && (
                    <div className={classes.historyTabBody}>
                      <WideHistoryContent
                        lead={lead}
                        refreshKey={historyRefreshKey}
                      />
                    </div>
                  )}
                  {selectTabKey === '4' && (
                    <div className={classes.wideAboutRoot}>
                      <UtmTagsContent
                        fields={leadFields}
                        fieldsData={formData}
                        updateFieldData={handleChangeFieldData}
                      />
                    </div>
                  )}
                </div>
              </LeadStatusProvider>
            </div>

            {(access.canViewControlButtons || access.canEditFields) && (
              <div className={classes.controlButtonList}>
                <Button
                  variant="action"
                  color="secondary"
                  leftContent={<SaveOutlined {...antIconPointerProps} />}
                  onClick={() => handlerSaveData()}
                >
                  Сохранить
                </Button>
                <Button
                  variant="action"
                  color="gray"
                  leftContent={<CloseCircleOutlined {...antIconPointerProps} />}
                  onClick={() => onClose()}
                >
                  Отмена
                </Button>
                {access.canViewControlButtons && leadStateForWorkflow === 'new' && (
                  <Button
                    variant="action"
                    color="gray"
                    leftContent={<UserAddOutlined {...antIconPointerProps} />}
                    onClick={handleMoveToInWorkClick}
                  >
                    В работу
                  </Button>
                )}
                {access.canViewControlButtons && leadStateForWorkflow === 'in_work' && (
                  <>
                    <Button
                      variant="action"
                      color="gray"
                      onClick={() =>
                        setOpenModal({ type: 'change-lead-state' })
                      }
                      leftContent={<InboxOutlined {...antIconPointerProps} />}
                    >
                      В обработанные
                    </Button>
                    <Button
                      variant="action"
                      color="gray"
                      leftContent={
                        <UsergroupAddOutlined {...antIconPointerProps} />
                      }
                      onClick={() =>
                        setOpenModal({ type: 'change-lead-state-in-client' })
                      }
                    >
                      В клиенты
                    </Button>
                  </>
                )}
                {access.canViewControlButtons && leadStateForWorkflow === 'processed' && (
                  <>
                    <Button
                      variant="action"
                      color="gray"
                      leftContent={<RollbackOutlined {...antIconPointerProps} />}
                      onClick={() =>
                        setOpenModal({ type: 'change-lead-state' })
                      }
                    >
                      Вернуть в работу
                    </Button>
                    <Button
                      variant="action"
                      color="gray"
                      leftContent={
                        <UsergroupAddOutlined {...antIconPointerProps} />
                      }
                      onClick={() =>
                        setOpenModal({ type: 'change-lead-state-in-client' })
                      }
                    >
                      В клиенты
                    </Button>
                  </>
                )}
                {access.canViewControlButtons && leadStateForWorkflow === 'client' && (
                  <Button
                    variant="action"
                    color="gray"
                    leftContent={<UserAddOutlined {...antIconPointerProps} />}
                    onClick={() => setOpenModal({ type: 'change-lead-state' })}
                  >
                    Вернуть в работу
                  </Button>
                )}
                {access.canDelete && (
                  <Button
                    variant="action"
                    color="gray"
                    leftContent={<DeleteOutlined {...antIconPointerProps} />}
                    onClick={() => setOpenModal({ type: 'delete-lead' })}
                  >
                    Удалить
                  </Button>
                )}
                {canUseFunnels && (
                  <Button
                    variant="action"
                    color="gray"
                    leftContent={<FunnelPlotOutlined {...antIconPointerProps} />}
                    onClick={() => setOpenModal({ type: 'add-funnel' })}
                  >
                    Добавить воронку
                  </Button>
                )}
                {canRemoveFunnelsInCard && (
                  <Button
                    variant="action"
                    color="gray"
                    leftContent={<FunnelPlotOutlined {...antIconPointerProps} />}
                    onClick={() => setOpenModal({ type: 'remove-funnel' })}
                  >
                    Удалить воронку
                  </Button>
                )}
              </div>
            )}
          </div>

          {isLeadModalLoading && (
            <div className={classes.loadingOverlay}>
              <Preloader />
            </div>
          )}
        </div>
      </>
    ) : (
      <p className={classes.messageNoLead}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={'Лид не найден'}
        />
      </p>
    )
  },
)

LeadProfileModalContent.displayName = 'LeadProfileModalContent'

export default LeadProfileModalContent
